import React from "react";
import { Button, Stack, Typography } from "@mui/material";
import { ConfirmDialog } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import WorkflowRequestStatus from "enums/WorkflowRequestStatus";
import Summary, { SummaryItem } from "components/common/Summary";
import { Inline } from "components/layout/reusableLayouts";
import CustomPanel from "./CustomPanel";
import PolicyIcons from "./PolicyIcons";
import ViolationStatus from "enums/ViolationStatus";

/** One approver, as the `Approval` type in `WorkflowRequest.graphql`. */
export interface WorkflowApproval {
  userId: string;
  role: string;
  userName?: string | null;
}

/**
 * The fields `GET_PAYMENT` asks for off a workflow request. `canceledBy` is a
 * single `Approval` where the other two are lists; the schema says so.
 */
export interface WorkflowRequest {
  id?: string;
  status?: WorkflowRequestStatus | string;
  approvalType?: string;
  approvedBy?: WorkflowApproval[] | null;
  approvedDate?: number | null;
  rejectedBy?: WorkflowApproval[] | null;
  rejectedDate?: number | null;
  canceledBy?: WorkflowApproval | null;
  canceledDate?: number | null;
  remainingApprovalRoles?: string[] | null;
  userId?: string | null;
  isApprovalAllowed?: boolean | null;
}

interface ApprovalDetailsProps {
  workflowRequest?: WorkflowRequest | null;
  loadingApproveReject?: boolean;
  onApprove?: () => void;
  onReject?: () => void | Promise<void>;
}

const ApprovalDetails: React.FC<ApprovalDetailsProps> = ({
  workflowRequest,
  loadingApproveReject,
  onApprove,
  onReject,
}) => {
  const { t } = useTranslation();
  const [confirmingReject, setConfirmingReject] = React.useState(false);
  const [rejecting, setRejecting] = React.useState(false);

  const reject = async () => {
    setRejecting(true);
    try {
      await onReject?.();
    } finally {
      setRejecting(false);
      setConfirmingReject(false);
    }
  };

  const isApprovalAllowed = workflowRequest?.isApprovalAllowed;
  const isApproved = workflowRequest?.status === WorkflowRequestStatus.APPROVED;

  const workflowRequestItems: SummaryItem[] = [
    {
      title: t("Remaining Approvals"),
      value:
        workflowRequest?.status === "PENDING"
          ? workflowRequest?.remainingApprovalRoles?.join(", ")
          : null,
    },
    {
      title: t("Approved By"),
      value: workflowRequest?.approvedBy
        ?.map((approver) => approver.userName)
        .join(", "),
    },
    {
      title: t("Rejected By"),
      value: workflowRequest?.rejectedBy
        ?.map((rejecter) => rejecter.userName)
        .join(", "),
    },
    {
      title: t("Cancelled By"),
      value: workflowRequest?.canceledBy?.userName,
    },
  ];

  return (
    <CustomPanel>
      <Stack sx={{ padding: "var(--space-5)", gap: "var(--space-5)" }}>
        <Inline gap="var(--space-3)">
          <PolicyIcons
            violationStatus={
              isApproved ? ViolationStatus.MATCH : ViolationStatus.MISSING
            }
            violationType="approval"
            size={16}
          />
          <Typography variant="h6">{t("Approval Details")}</Typography>
        </Inline>
        <Summary
          items={workflowRequestItems}
          disableContainer={true}
          children={
            workflowRequest?.status === WorkflowRequestStatus.PENDING &&
            (onApprove || onReject) &&
            isApprovalAllowed && (
              <Inline gap="var(--space-4)" justify="center" wrap={false}>
                {onApprove && (
                  <Button
                    variant="primary-filled"
                    fullWidth
                    size="small"
                    onClick={onApprove}
                    disabled={loadingApproveReject}
                  >
                    {t("Approve")}
                  </Button>
                )}
                {onReject && (
                  <Button
                    variant="danger-outlined"
                    fullWidth
                    size="small"
                    onClick={() => setConfirmingReject(true)}
                    disabled={loadingApproveReject}
                  >
                    {t("Reject")}
                  </Button>
                )}
              </Inline>
            )
          }
        />
      </Stack>
      <ConfirmDialog
        open={confirmingReject}
        title={t("approvalFeature.rejectTitle")}
        message={t("approvalFeature.rejectMessage")}
        confirmLabel={t("Reject")}
        cancelLabel={t("Cancel")}
        busy={rejecting}
        onConfirm={reject}
        onCancel={() => {
          if (!rejecting) setConfirmingReject(false);
        }}
      />
    </CustomPanel>
  );
};

export default ApprovalDetails;
